import React, { useEffect } from 'react';

import { MountState, useAsyncUpdate } from './effects-helper';

export type SetTimeoutReturn = ReturnType<typeof setTimeout> | null;

export const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const waitRequestAnimationFrame = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });

export interface Timeout {
  setTimeout: (callback: (ms: MountState) => void, delay?: number | undefined) => Promise<MountState>;
  clearTimeout: () => void;
  getCurrentTimeout: () => SetTimeoutReturn;
}

export const useTimeout = (): Timeout => {
  const timeoutRef = React.useRef<SetTimeoutReturn>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const clearCurrentTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const setCurrentTimeout = useAsyncUpdate(
    (ms, callback: (ms: MountState) => void, delay: number = 0) =>
      new Promise<MountState>((resolve) => {
        clearCurrentTimeout();
        timeoutRef.current = setTimeout(() => {
          callback(ms);
          timeoutRef.current = null;
          resolve(ms);
        }, delay);
      }),
  );

  return {
    setTimeout: setCurrentTimeout,
    clearTimeout: clearCurrentTimeout,
    getCurrentTimeout: () => timeoutRef.current,
  };
};

const areAnimationValuesEqual = <T>(first: T, second: T) => {
  if (Array.isArray(first) && Array.isArray(second)) {
    return first.length === second.length && first.every((item, index) => item === second[index]);
  }

  return first === second;
};

/**
 * This is hook that keep value for a short time after it changes to allow animation of the change.
 * If another value is set during the animation, the previous value will be immediately replaced with the new value
 * and the animation will be restarted.
 * Arrays are compared by their items, so an array that is recreated on every render does not restart the animation.
 * @param value Original value that should be kept for animation
 * @param delay Animation delay in ms, default is 150ms
 * @returns Value that is kept for animation, it will be the same as original value after the delay
 */
export const useAnimationState = <T>(value: T, delay: number = 150) => {
  const [animationValue, setAnimationValue] = React.useState(value);
  const pendingValue = React.useRef(value);
  const timeout = useTimeout();

  useEffect(() => {
    if (areAnimationValuesEqual(pendingValue.current, value)) {
      return;
    }

    // The value that is still waiting for its delay is applied immediately, so the new animation starts from it
    if (!areAnimationValuesEqual(pendingValue.current, animationValue)) {
      setAnimationValue(pendingValue.current);
    }

    pendingValue.current = value;
    timeout.setTimeout((ms) => {
      if (ms.isMounted) {
        setAnimationValue(value);
      }
    }, delay);
  });

  return animationValue;
};
